import React, { ReactElement, useId, useState } from 'react'
import Header from 'src/components/Header/Header'

import CardActionsMenu from './CardActionsMenu/CardActionsMenu'
import CardArt from './CardArt/CardArt'
import * as style from './CardDetailHero.style'
import CardDisplayActions from './CardDisplayActions/CardDisplayActions'
import CardRevealPanel from './CardRevealPanel/CardRevealPanel'
import CardSummary from './CardSummary/CardSummary'
import { useCardDetailHeroData } from './useCardDetailHeroData'
import { useCardRevealDisclosure } from './useCardRevealDisclosure'

const CardDetailHero: React.FC = (): ReactElement => {
  const { card, billing, credentials } = useCardDetailHeroData()

  // Per-instance id, not a module constant, so the (future) compact hero can
  // mount alongside this one without two panels sharing an id — which would make
  // each Show details trigger's aria-controls resolve to the wrong panel.
  const revealPanelId = useId()

  // The shell owns card status so the summary pill, the card-art overlay, and
  // the freeze button label always agree — the design spec's "one owner" rule.
  // Demo-stub: Freeze toggles active⇄frozen locally; issuer-imposed LOCKED is
  // left unchanged (its action is "Request unlock", not a voluntary freeze).
  const [status, setStatus] = useState(card.status)
  const toggleFreeze = (): void =>
    setStatus((current) =>
      current === 'ACTIVE'
        ? 'FROZEN'
        : current === 'FROZEN'
          ? 'ACTIVE'
          : current,
    )

  // The shell also owns the reveal disclosure (same "one owner" rule), so the
  // full hero and the future compact hero will share one open state.
  const reveal = useCardRevealDisclosure()

  return (
    // Reuse the legacy Header layout so the hero occupies the exact page band
    // (white background + standard responsive header padding) the AccountHeader
    // it replaces did — keeping horizontal alignment and the gap to the Subnav
    // below identical across breakpoints, rather than re-deriving that padding.
    <Header>
      <section css={style.hero} data-testid="card-detail-hero">
        <div css={style.left} data-testid="card-detail-hero-left">
          <CardSummary status={status} />
        </div>
        <div
          css={style.right}
          data-testid="card-detail-hero-right"
          ref={reveal.containerRef}
        >
          <div css={style.toolbar} data-testid="card-detail-hero-toolbar">
            <CardDisplayActions
              status={status}
              onFreeze={toggleFreeze}
              revealOpen={reveal.open}
              onToggleReveal={reveal.toggle}
              revealPanelId={revealPanelId}
            />
            {/*
              Ungated: no `capabilities` passed → all five actions show. When the
              real CapabilityDecisions endpoint lands, pass the per-action verdict
              here. Do not branch on role names — the server is the sole authority.
            */}
            <CardActionsMenu />
          </div>
          <CardArt
            status={status}
            network={card.network}
            last4={card.last4}
            holder={card.cardholder.displayName}
            pan={credentials.pan}
            exp={credentials.exp}
            revealed={reveal.open}
          />
          {reveal.open && (
            <CardRevealPanel
              id={revealPanelId}
              cardholderName={card.cardholder.displayName}
              address={billing.address}
              pan={credentials.pan}
              cvc={credentials.cvc}
            />
          )}
        </div>
      </section>
    </Header>
  )
}

export default CardDetailHero
